import { useDtoolsPdfPackageMode } from "@/components/sales/dtools/DtoolsPdfPackageContext";
import { PROPOSAL_COPY_FONT } from "@/lib/proposalTheme";
import { cn } from "@/lib/utils";
import { shouldIncludeExecutiveOverview, type DtoolsDisplaySettings } from "@shared/dtoolsDisplaySettings";
import { resolveSystemSummaryExecutiveOverview } from "@shared/dtoolsProposalPresentation";
import type { DtoolsProposalPresentation } from "@shared/dtoolsProposalPresentation";
import type { SystemSummaryTabContent } from "@shared/dtoolsSystemSummary";
import { PROPOSAL_RETAIL_PRICE_DISCLAIMER } from "@shared/proposalRetailPriceDisclaimer";
import type { ProposalCustomerPricingDisplay } from "@shared/proposalCustomerPricing";
import type { DtoolsOptionSectionActionsProps } from "@/components/sales/dtools/DtoolsOptionSectionActions";
import DtoolsPdfSystemsIncludedTable from "@/components/sales/dtools/DtoolsPdfSystemsIncludedTable";
import ProposalConsolidatedSystemSummaryTable, {
  type ConsolidatedSystemSummaryRow,
} from "@/components/proposal/ProposalConsolidatedSystemSummaryTable";
import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";
import { PDF_BODY_CLASS, PDF_SECTION_TITLE_CLASS } from "@/lib/proposalPdfTypography";

type Props = {
  content: SystemSummaryTabContent;
  presentation?: DtoolsProposalPresentation;
  displaySettings?: DtoolsDisplaySettings;
  /** Flat system rows for the consolidated System Summary table. */
  consolidatedSystemRows?: ConsolidatedSystemSummaryRow[];
  proposalPricing?: ProposalCustomerPricingDisplay | null;
  allowIndividualSystemApproval?: boolean;
  /** When true (multiple-options proposals), omit combined grand total from the table. */
  hideCombinedGrandTotal?: boolean;
  optionActions?: Omit<DtoolsOptionSectionActionsProps, "optionLabel">;
  showRetailPriceDisclaimer?: boolean;
  className?: string;
  testId?: string;
};

/** Shared System Summary tab — executive overview before per-system detail tabs. */
export default function ProposalSystemSummaryTab({
  content,
  presentation,
  displaySettings,
  consolidatedSystemRows,
  proposalPricing,
  allowIndividualSystemApproval = false,
  hideCombinedGrandTotal = false,
  optionActions,
  showRetailPriceDisclaimer = false,
  className,
  testId = "dtools-system-summary-tab",
}: Props) {
  const pdfPackageMode = useDtoolsPdfPackageMode();
  const showExecutiveOverview = shouldIncludeExecutiveOverview(displaySettings);
  const executiveOverview = resolveSystemSummaryExecutiveOverview(presentation);
  const systems = consolidatedSystemRows ?? [];
  const positiveSystems = systems.filter((row) => Number.isFinite(row.amount) && row.amount > 0.004);
  const systemsTotal = positiveSystems.reduce((sum, row) => sum + row.amount, 0);
  const showDiscount =
    !hideCombinedGrandTotal &&
    proposalPricing?.showFinalAgreedPrice === true &&
    Number.isFinite(proposalPricing.proposalTotal) &&
    Number.isFinite(proposalPricing.finalAgreedPrice) &&
    proposalPricing.proposalTotal - proposalPricing.finalAgreedPrice > 0.004;
  const showGst =
    !hideCombinedGrandTotal &&
    proposalPricing?.showGst === true &&
    Number.isFinite(proposalPricing.gstAmount) &&
    proposalPricing.gstAmount > 0.004;
  const grandTotal =
    showGst && Number.isFinite(proposalPricing?.finalAmountPayable)
      ? proposalPricing!.finalAmountPayable
      : proposalPricing?.showFinalAgreedPrice === true && Number.isFinite(proposalPricing.finalAgreedPrice)
      ? proposalPricing.finalAgreedPrice
      : systemsTotal;
  const grandLabel =
    showGst
      ? "Final Amount Payable"
      : proposalPricing?.showFinalAgreedPrice === true
        ? "Final Price"
        : "Grand Total";

  return (
    <section
      className={cn(
        "proposal-system-summary",
        pdfPackageMode ? "space-y-4" : "space-y-10",
        className,
      )}
      data-testid={testId}
      style={{ fontFamily: PROPOSAL_COPY_FONT }}
    >
      {!pdfPackageMode ? (
        <div className="space-y-3">
          <h2 className="text-xl font-semibold tracking-tight text-[#1A2744]">System Summary</h2>
          <p className="text-sm text-[#1A2744]/70">
            A detailed overview of what this proposal includes before you review each system in detail.
          </p>
        </div>
      ) : null}

      {systems.length > 0 ? (
        pdfPackageMode ? (
          <DtoolsPdfSystemsIncludedTable
            rows={content.systemsIncludedRows}
            grandTotalLabel={hideCombinedGrandTotal ? undefined : grandLabel}
            grandTotalAmount={
              hideCombinedGrandTotal
                ? null
                : formatDtoolsMoney(
                    showDiscount && proposalPricing?.finalAgreedPrice != null
                      ? proposalPricing.finalAgreedPrice
                      : grandTotal,
                  )
            }
          />
        ) : (
          <ProposalConsolidatedSystemSummaryTable
            systemRows={systems}
            proposalPricing={proposalPricing}
            allowIndividualSystemApproval={allowIndividualSystemApproval}
            hideCombinedGrandTotal={hideCombinedGrandTotal}
            optionActions={optionActions}
            testId="proposal-summary-amounts"
          />
        )
      ) : null}

      {showExecutiveOverview && executiveOverview ? (
        <section className="space-y-2" data-testid="proposal-executive-overview">
          <h3 className={pdfPackageMode ? PDF_SECTION_TITLE_CLASS : "text-base font-semibold text-[#1A2744]"}>
            Overview
          </h3>
          <div
            className={
              pdfPackageMode
                ? "rounded-lg border border-[#1A2744]/10 bg-white p-3"
                : "rounded-xl border border-[#1A2744]/10 bg-white p-4 sm:p-5"
            }
          >
            <div
              className={
                pdfPackageMode
                  ? `max-w-none whitespace-pre-wrap ${PDF_BODY_CLASS} ${
                      (executiveOverview?.length ?? 0) > 700 ? "columns-2 gap-4" : ""
                    }`
                  : "prose prose-sm max-w-none whitespace-pre-wrap text-[#1A2744]/85"
              }
            >
              {executiveOverview}
            </div>
          </div>
        </section>
      ) : null}

      {content.systemsIncludedRows.length > 0 && !pdfPackageMode ? (
        <section className="space-y-4" data-testid="proposal-systems-included">
          <h3 className="text-base font-semibold text-[#1A2744]">Systems Included</h3>
          <div className="space-y-4" data-testid="dtools-systems-included-cards">
            {content.systemsIncludedRows.map((row) => (
              <article
                key={row.key}
                className="pdf-keep-together rounded-xl border border-[#1A2744]/10 bg-white p-4 sm:p-5"
                data-testid={`dtools-system-included-card-${row.key}`}
              >
                <div className="space-y-4">
                  <div className="space-y-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#1A2744]/55">System</p>
                    <p className="text-base font-semibold text-[#1A2744]">{row.system}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#1A2744]/55">
                      Description
                    </p>
                    <p className="text-sm leading-relaxed text-[#1A2744]/75">{row.description}</p>
                  </div>
                  {row.amount ? (
                    <div className="space-y-1 border-t border-[#1A2744]/8 pt-3">
                      <p className="text-lg font-semibold tabular-nums text-[#1A2744]">{row.amount}</p>
                      {showRetailPriceDisclaimer ? (
                        <p className="text-xs leading-relaxed text-[#1A2744]/65">{PROPOSAL_RETAIL_PRICE_DISCLAIMER}</p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </section>
  );
}
